const DAY = 86400000;

const dateKey = (date) => date.toISOString().slice(0, 10);
const civilDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 12));
const nextDate = (date) => new Date(date.getTime() + DAY);

/**
 * USPS operating holidays. A fixed-date Saturday holiday stays Saturday, and
 * a fixed-date Sunday holiday also closes Monday.
 */
export function getUSPSHolidays(year) {
  const holidays = new Set();
  const addFixed = (month, day) => {
    const date = civilDate(year, month, day);
    holidays.add(dateKey(date));
    if (date.getUTCDay() === 0) holidays.add(dateKey(nextDate(date)));
  };
  const addMonday = (month, occurrence) => {
    const first = civilDate(year, month, 1);
    holidays.add(dateKey(civilDate(year, month, 1 + ((8 - first.getUTCDay()) % 7) + 7 * (occurrence - 1))));
  };

  addFixed(1, 1);
  addMonday(1, 3);
  addMonday(2, 3);
  const mayEnd = civilDate(year, 5, 31);
  holidays.add(dateKey(civilDate(year, 5, 31 - ((mayEnd.getUTCDay() + 6) % 7))));
  addFixed(6, 19);
  addFixed(7, 4);
  addMonday(9, 1);
  addMonday(10, 2);
  addFixed(11, 11);
  const november = civilDate(year, 11, 1);
  holidays.add(dateKey(civilDate(year, 11, 1 + ((11 - november.getUTCDay()) % 7) + 21)));
  addFixed(12, 25);
  return holidays;
}

/**
 * Calculate against store-local civil dates, independent of the shopper's time zone.
 * Dispatch is Monday-Friday. Transit is Monday-Saturday. The dispatch day is day zero.
 */
export function calculateDeliveryEstimate(now, config) {
  try {
    if (!(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
    const { timeZone, cutoffHour, cutoffMinute, transitDays, processingDays } = config;
    if (
      !timeZone ||
      !Number.isInteger(cutoffHour) || cutoffHour < 0 || cutoffHour > 23 ||
      !Number.isInteger(cutoffMinute) || cutoffMinute < 0 || cutoffMinute > 59 ||
      !Number.isInteger(transitDays) || transitDays < 1 || transitDays > 30 ||
      !Number.isInteger(processingDays) || processingDays < 0 || processingDays > 30
    ) return null;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, Number(part.value)]));
    const order = civilDate(values.year, values.month, values.day);

    const closures = new Set();
    for (const value of (config.closures || '').split(/[\s,]+/).filter(Boolean)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
      const [year, month, day] = value.split('-').map(Number);
      if (dateKey(civilDate(year, month, day)) !== value) return null;
      closures.add(value);
    }

    const calendars = new Map();
    const isHoliday = (date) => {
      const year = date.getUTCFullYear();
      if (!calendars.has(year)) calendars.set(year, getUSPSHolidays(year));
      return calendars.get(year).has(dateKey(date));
    };
    const canDispatch = (date) => {
      const weekday = date.getUTCDay();
      return weekday > 0 && weekday < 6 && !isHoliday(date) && !closures.has(dateKey(date));
    };
    const canTransit = (date) => date.getUTCDay() !== 0 && !isHoliday(date);

    let ship = order;
    if (values.hour * 60 + values.minute >= cutoffHour * 60 + cutoffMinute) ship = nextDate(ship);
    let searched = 0;
    while (!canDispatch(ship)) {
      if (++searched > 366) return null;
      ship = nextDate(ship);
    }
    for (let remaining = processingDays; remaining > 0;) {
      ship = nextDate(ship);
      if (++searched > 366) return null;
      if (canDispatch(ship)) remaining--;
    }

    let delivery = ship;
    for (let remaining = transitDays; remaining > 0;) {
      delivery = nextDate(delivery);
      if (++searched > 732) return null;
      if (canTransit(delivery)) remaining--;
    }

    const orderDate = dateKey(order);
    const shipDate = dateKey(ship);
    return {
      orderDate,
      shipDate,
      deliveryDate: dateKey(delivery),
      shipRelative: shipDate === orderDate ? 'today' : shipDate === dateKey(nextDate(order)) ? 'tomorrow' : 'date',
    };
  } catch (_) {
    return null;
  }
}

const HTMLElementBase = globalThis.HTMLElement || class {};

export class DFGDeliveryEstimate extends HTMLElementBase {
  constructor() {
    super();
    this.timer = null;
    this.productRoot = null;
    this.variantSelect = null;
    this.variants = [];
  }

  connectedCallback() {
    this.productRoot = this.closest('[data-dfg-product]');
    this.variantSelect = this.productRoot?.querySelector('[data-dfg-variant-select]') || null;
    const dataElement = this.productRoot?.querySelector('[data-dfg-variants]');
    try {
      this.variants = dataElement ? JSON.parse(dataElement.textContent) : [];
    } catch (_) {
      this.variants = [];
    }

    this.variantSelect?.addEventListener('change', this.onVariantChange);
    document.addEventListener('visibilitychange', this.refresh);
    window.addEventListener('pageshow', this.refresh);
    this.syncVariantEligibility();
    clearInterval(this.timer);
    this.timer = window.setInterval(this.refresh, 60000);
  }

  disconnectedCallback() {
    this.variantSelect?.removeEventListener('change', this.onVariantChange);
    document.removeEventListener('visibilitychange', this.refresh);
    window.removeEventListener('pageshow', this.refresh);
    clearInterval(this.timer);
  }

  onVariantChange = () => this.syncVariantEligibility();

  syncVariantEligibility() {
    if (!this.variantSelect || !this.variants.length) {
      this.dataset.eligible = 'false';
      this.refresh();
      return;
    }

    const variant = this.variants.find((item) => String(item.id) === this.variantSelect.value);
    this.dataset.eligible = variant?.deliveryEligible === true ? 'true' : 'false';
    this.refresh();
  }

  refresh = () => {
    if (this.dataset.eligible !== 'true' || !this.productRoot) {
      this.hidden = true;
      return;
    }
    const required = [this.dataset.cutoffHour, this.dataset.cutoffMinute, this.dataset.transitDays, this.dataset.processingDays];
    if (required.some((value) => value == null || value.trim() === '')) {
      this.hidden = true;
      return;
    }

    const estimate = calculateDeliveryEstimate(new Date(), {
      timeZone: this.dataset.timeZone || '',
      cutoffHour: Number(this.dataset.cutoffHour),
      cutoffMinute: Number(this.dataset.cutoffMinute),
      transitDays: Number(this.dataset.transitDays),
      processingDays: Number(this.dataset.processingDays),
      closures: this.dataset.closures,
    });
    if (!estimate) {
      this.hidden = true;
      return;
    }

    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
        weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
      });
    } catch (_) {
      formatter = new Intl.DateTimeFormat('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
      });
    }
    const formatDate = (key) => formatter.format(new Date(`${key}T12:00:00Z`));
    const deliveryDate = this.querySelector('[data-dfg-delivery-date]');
    const dispatch = this.querySelector('[data-dfg-delivery-dispatch]');
    if (!deliveryDate || !dispatch) {
      this.hidden = true;
      return;
    }

    const deliveryText = formatDate(estimate.deliveryDate);
    const dispatchText = estimate.shipRelative === 'today'
      ? this.dataset.shipsToday || ''
      : estimate.shipRelative === 'tomorrow'
        ? this.dataset.shipsTomorrow || ''
        : (this.dataset.shipsOn || '').replace('__DFG_DATE__', formatDate(estimate.shipDate));

    if (deliveryDate.dateTime !== estimate.deliveryDate) deliveryDate.dateTime = estimate.deliveryDate;
    if (deliveryDate.textContent !== deliveryText) deliveryDate.textContent = deliveryText;
    if (dispatch.textContent !== dispatchText) dispatch.textContent = dispatchText;
    this.hidden = false;
  };
}

if (globalThis.customElements && !customElements.get('dfg-delivery-estimate')) {
  customElements.define('dfg-delivery-estimate', DFGDeliveryEstimate);
}
